import { useNotification } from 'hooks'
import s from '../../../commonStyles.modules.scss'
import {useMemo, useState} from 'react'
import { Table } from 'components/core-ui/Table'
import { columns, drawerFilters, toolbarActions } from './helpers'
import { makeCoreGetFileRequest, makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { useQuery } from 'react-query'
import { formatAndDownloadResponse } from 'utils/helpers'
import { getDepositCodeOptions } from "../../api";
import { useSearchParams } from "react-router-dom";

const DifferencesProducts = ({ isHistorical }) => {
  const { showNotification } = useNotification()
  const [tableItems, setTableItems] = useState<DifferencesProduct[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [downloadImageId, setDownloadImageId] = useState(null)
  const [isExcelExportLoading, setIsExcelExportLoading] = useState<boolean>(false)
  const [searchParams] = useSearchParams()
  const filtersParam = searchParams.get('filters')

  let selectedRow
  if (filtersParam) {
    try {
      selectedRow = JSON.parse(decodeURIComponent(filtersParam))
    } catch (error) {
      showNotification({
        message: `Error parsing filters. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const { data: depositOptions } = getDepositCodeOptions(showNotification)

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: isHistorical
            ? '/shop-reception/productWithDifferenceArchive'
            : '/shop-reception/productWithDifference',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `Error while fetching table data. ${formatErrorMessage(error)}`,
        type: 'error'
      })
      setIsLoading(false)
    }
  }

  const { data: shopCodes } = useQuery(
    ['shop-codes'],
    async () => {
      const result = await makeCoreGetRequest('/shop-reception/shops/codes')

      const resultOptions = result.list.map(item => ({
        label: item.displayName,
        value: item.code
      }))
      return resultOptions || []
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching shop codes dropdown data. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { refetch: refetchDownloadImage } = useQuery(
    ['download-image'],
    async () => {
      setDownloadImageId(null)
      const url = toTableGETUrl({
        url: isHistorical
          ? `/shop-reception/productWithDifferenceArchive/downloadArchive/${downloadImageId}`
          : `/shop-reception/productWithDifference/downloadArchive/${downloadImageId}`
      })
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      return formatAndDownloadResponse(response)
    },
    {
      onError: error => {
        showNotification({
          message: `Error while downloading file. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      enabled: downloadImageId === null ? false : true
    }
  )

  const exportFileExcel = async (tableState, e) => {
    setIsExcelExportLoading(true)
    const { filters, order, orderBy } = tableState
    const url = toTableGETUrl({
      url: isHistorical
        ? '/shop-reception/productWithDifference/export'
        : '/shop-reception/productWithDifference/export',
      tableFilters: filters,
      order,
      orderBy
    })

    try {
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      formatAndDownloadResponse(response);
      setIsExcelExportLoading(false)
    } catch (error) {
      setIsExcelExportLoading(false)
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const toolbar = useMemo(() => toolbarActions({
      totalCount,
      exportFileExcel: exportFileExcel,
      isExcelExportLoading }),
    [totalCount, isExcelExportLoading])

  const { data: metiRayons } = useQuery(
    ['meti-rayons'],
    async () => {
      const data = await makeCoreGetRequest(`/shop-reception/products/meti-rayons`)
      return data.map(item => ({
        label: item.displayValue,
        value: item.code
      }))
    },
    {
      onError: error => {
        showNotification({
          message: `A apărut o eroare la obtinerea raioanelor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  return (
    <div className={s.mainContainer}>
      <Table
        hideCheckbox
        columns={columns(setDownloadImageId)}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        toolbarActions={toolbar}
        rowActionsWidth="130px"
        filters={drawerFilters(shopCodes, metiRayons, depositOptions, selectedRow)}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default DifferencesProducts
